/* eslint-disable no-throw-literal */
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import FormData from "form-data";

// Assuming PrefData and other dependencies are available
// import { getSessionToken } from './prefdata';
// import { isNetworkAvailable } from './network_status';
import { BASE_URL } from "../configs";
import { getAccessToken } from "../pages/Protected";

const isNetworkAvailable = () => true;

enum HttpMethod {
  GET = "GET",
  POST = "POST",
  DELETE = "DELETE",
  PUT = "PUT",
}

const axiosCache = new Map<
  string,
  { response: AxiosResponse; timestamp: number }
>();

const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

async function getCachedAxiosResponse(
  config: AxiosRequestConfig,
  ttl = DEFAULT_CACHE_TTL,
): Promise<AxiosResponse> {
  const key = generateCacheKey(config);

  if (config.method?.toUpperCase() === "GET" && axiosCache.has(key)) {
    const cached = axiosCache.get(key)!;
    const isExpired = Date.now() - cached.timestamp > ttl;

    // if (!isExpired) {
    //   return cached.response;
    // } else {
    //   axiosCache.delete(key); // Clean up expired entry
    // }

    return cached.response;
  }

  const response = await axios(config);

  if (config.method?.toUpperCase() === "GET") {
    axiosCache.set(key, {
      response,
      timestamp: Date.now(),
    });
  }

  return response;
}

export async function buildHeaderTokens(
  isStripePayment: boolean = false,
  request?: any,
): Promise<Record<string, string>> {
  const userToken = getAccessToken()?.toString() || "";

  const header: Record<string, string> = {
    Authorization: `${userToken}`,
    Accept: "application/json; charset=utf-8",
  };

  return header;
}

async function buildHttpResponse({
  endPoint,
  method = HttpMethod.GET,
  body,
  isStripePayment = false,
  customHeader,
  cacheTTL,
  noCache = false,
}: {
  endPoint: string;
  method?: HttpMethod;
  body?: any;
  isStripePayment?: boolean;
  customHeader?: Record<string, string>;
  cacheTTL?: number;
  noCache?: boolean;
}): Promise<any> {
  if (await isNetworkAvailable()) {
    const headers = await buildHeaderTokens(isStripePayment, body);
    const url = buildBaseUrl(endPoint);

    const config: AxiosRequestConfig = {
      method,
      url,
      headers: customHeader ?? headers,
      data: body,
      responseType: "json",
    };

    try {
      let response: AxiosResponse;

      if (noCache) {
        // Skip cache entirely for this request
        response = await axios(config);
      } else {
        // Use cached response as usual
        response = await getCachedAxiosResponse(config, cacheTTL);
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error(
            `Error (${method}) ${error.response.status}: ${error.response.data}`,
          );
          return {
            success: false,
            status: error.response.status,
            message: error.response.data?.message || "Something Went Wrong",
            data: error.response.data,
          };
        } else {
          return {
            success: false,
            status: null,
            message: "Your internet is not working",
            data: null,
          };
        }
      } else {
        return {
          success: false,
          status: null,
          message: "An unknown error occurred",
          data: null,
        };
      }
    }
  } else {
    throw "Your internet is not working";
  }
}

export function buildBaseUrl(endPoint: string): string {
  if (!endPoint.startsWith("http")) return `${BASE_URL}/${endPoint}`;
  return endPoint;
}

async function handleResponse(
  response: AxiosResponse,
  avoidTokenError?: boolean,
): Promise<any> {
  if (!(await isNetworkAvailable())) {
    throw "Your internet is not working";
  }
  if (response.status === 401) {
    throw "Token Expired";
  }

  if (response.status >= 200 && response.status <= 206) {
    return response.data;
  } else {
    throw response.data.message || "Something Went Wrong";
  }
}

function handleErrorResponse(response: AxiosResponse): Record<string, any> {
  return {
    status: response.status,
    message: "Something Went Wrong",
  };
}

export async function buildFormDataRequest({
  endpoint,
  method = "GET",
  body = null,
  customUrl,
}: {
  endpoint?: string;
  method?: string;
  body?: any;
  customUrl?: string;
}) {
  const formData = new FormData();
  Object.keys(body).forEach((key) => formData.append(key, body[key]));
  const response = await fetch(
    endpoint != null
      ? buildBaseUrl(endpoint)
      : customUrl != null
        ? customUrl
        : "",
    {
      method: method,
      headers: await buildHeaderTokens(),
      body: formData as any,
    },
  );

  return await response.json();
}

export async function buildMultipartHttpResponse({
  endPoint,
  method = HttpMethod.POST,
  formData,
  isStripePayment = false,
  customHeader,
}: {
  endPoint: string;
  method?: HttpMethod;
  formData: FormData;
  isStripePayment?: boolean;
  customHeader?: Record<string, string>;
}): Promise<any> {
  if (await isNetworkAvailable()) {
    const headers = await buildHeaderTokens(isStripePayment, formData);

    // Set Content-Type to multipart/form-data
    headers["Content-Type"] = "multipart/form-data";

    const url = buildBaseUrl(endPoint);

    const config: AxiosRequestConfig = {
      method,
      url,
      headers: customHeader ?? headers,
      data: formData,
      responseType: "json",
    };

    try {
      const response: AxiosResponse = await axios(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.error(
            `Error (${method}) ${error.response.status}: ${error.response.data}`,
          );
          throw error.response.data.message || "Something Went Wrong";
        } else {
          throw "Your internet is not working";
        }
      } else {
        throw "An unknown error occurred";
      }
    }
  } else {
    throw "Your internet is not working";
  }
}

/**
 * Dynamically builds a multipart/form-data request for file upload.
 * This function will allow you to upload a file with an optional title.
 * The user is authenticated using the Authorization token in the header.
 *
 * @param params - Object containing endpoint, method, file, title, and custom headers.
 */
async function uploadFileWithFormData({
  endpoint,
  method = HttpMethod.POST,
  file,
  title,
  customHeaders = {},
}: {
  endpoint: string;
  method?: HttpMethod; // Default to POST
  file: File;
  title?: string;
  customHeaders?: Record<string, string>;
}): Promise<any> {
  // Check if the network is available
  if (!isNetworkAvailable()) {
    throw "Your internet is not working";
  }

  // Build the FormData object for multipart/form-data
  const formData = new FormData();
  formData.append("file", file); // Append the file to the form data
  if (title) formData.append("title", title); // Append the title if provided

  // Build headers dynamically, including Authorization token
  const headers = {
    ...customHeaders,
    "Content-Type": "multipart/form-data",
    Authorization: `${getAccessToken() || ""}`, // Assuming you fetch the token using `getAccessToken()`
  };

  // Build Axios config
  const config: AxiosRequestConfig = {
    url: buildBaseUrl(endpoint),
    method: method, // POST by default
    headers,
    data: formData,
  };

  try {
    // Perform the file upload request
    const response: AxiosResponse = await axios(config);
    return handleResponse(response); // Handle success response
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Log detailed error if available
        console.error(
          `Error (${method}) ${error.response.status}: ${error.response.data}`,
        );
        throw error.response.data || "Something Went Wrong";
      } else {
        throw "Your internet is not working";
      }
    } else {
      throw "An unknown error occurred";
    }
  }
}

/**
 * Generates a cache key for a given request configuration
 * @param config - The axios request configuration
 * @returns A string that uniquely identifies this request
 */
export function generateCacheKey(config: AxiosRequestConfig): string {
  return (
    config.url +
    JSON.stringify(config.params || {}) +
    JSON.stringify(config.headers || {})
  );
}

/**
 * Invalidates a specific cache entry
 * @param url - The endpoint URL
 * @param params - Optional query parameters
 * @param headers - Optional request headers
 * @returns True if an entry was found and removed, false otherwise
 */
export function invalidateCache(
  url: string,
  params?: any,
  headers?: any,
): boolean {
  const key =
    url + JSON.stringify(params || {}) + JSON.stringify(headers || {});

  return axiosCache.delete(key);
}

/**
 * Invalidates all cache entries that match a URL pattern
 * @param urlPattern - String or RegExp to match against URLs
 * @returns Number of cache entries that were invalidated
 */
export function invalidateCacheByPattern(urlPattern: string | RegExp): number {
  let count = 0;
  const pattern =
    typeof urlPattern === "string"
      ? new RegExp(urlPattern.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"))
      : urlPattern;

  for (const key of axiosCache.keys()) {
    if (pattern.test(key)) {
      axiosCache.delete(key);
      count++;
    }
  }

  return count;
}

/**
 * Clears all cached responses
 */
export function clearCache(): void {
  axiosCache.clear();
}

/**
 * Gets information about the current cache state
 * @returns Object with cache statistics
 */
export function getCacheStats(): { size: number; keys: string[] } {
  return {
    size: axiosCache.size,
    keys: Array.from(axiosCache.keys()),
  };
}

export async function buildBlobHttpResponse({
  endPoint,
  method = HttpMethod.GET,
  body,
}: {
  endPoint: string;
  method?: HttpMethod;
  body?: any;
}): Promise<Blob> {
  if (!isNetworkAvailable()) throw "Your internet is not working";

  const headers = await buildHeaderTokens();
  const url = buildBaseUrl(endPoint);

  const response = await axios({
    method,
    url,
    headers,
    data: body,
    responseType: "blob",
  });

  return response.data;
}

export {
  buildHttpResponse,
  handleResponse,
  uploadFileWithFormData,
  HttpMethod,
  // buildMultipartRequest,
  handleErrorResponse,
};
